import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';

export interface ReportCompanyViews {
  company_id: number | null;
  company_name: string;
  views: number;
}

export interface ReportStats {
  total_views: number;
  unique_users: number;
  export_count: number;
  filters_used: boolean;
  sorts_used: boolean;
  views_by_company: ReportCompanyViews[];
}

/**
 * Fetch aggregated stats for a single report from the per-report stats endpoint.
 * When `companyId` is provided, the stats are scoped to that company's viewers
 * (used by the partner listing). Returns null on any failure so callers can show
 * an error/empty state.
 *
 * WarpDrive migration: this is a direct `fetch()` to a non-JSON:API stats
 * endpoint, so it can't go through `store.request()`. It builds the URL via
 * `buildBaseURL` (host from `setBuildURLConfig`) and the auth headers via
 * `buildTioAuthHeaders` — the established raw-fetch pattern (see
 * `requests/search.ts`) — replacing the deleted `report-tracking` adapter's
 * `.host`/`.headers`. `apiKey` is the per-app `ENV.apiKey` (tio-data is
 * owner-free — see `utils/auth-headers`); the auth token/x-auth-key come from
 * `auth-context` at call time.
 */
export async function fetchReportStats(
  reportId: string,
  apiKey: string,
  companyId?: string
): Promise<ReportStats | null> {
  if (!reportId) return null;

  const params = new URLSearchParams();
  params.set('filter[report_id]', reportId);
  if (companyId) params.set('filter[company_id]', companyId);

  // `{host}/api/v2/report-trackings/stats` — mirrors the legacy adapter's
  // `${adapter.host}/api/v2/report-trackings/stats`.
  const trackingsBase = buildBaseURL({
    identifier: { type: 'report-tracking' },
    op: 'query',
    resourcePath: 'api/v2/report-trackings',
  } satisfies QueryUrlOptions);
  const url = `${trackingsBase}/stats?${params.toString()}`;

  const headers = buildTioAuthHeaders({ apiKey, accept: 'application/json' });

  try {
    const response = await fetch(url, { headers });
    if (!response.ok) return null;

    const json = (await response.json()) as { data?: ReportStats };
    return json.data ?? null;
  } catch {
    return null;
  }
}
